import type { SystemUser } from '@engagement-web-app/common';
import { QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch } from '@tanstack/react-router';
import { Building2, ChartArea, FileSpreadsheet, LayoutDashboard, Users } from 'lucide-react';
import { useEffect } from 'react';
import { SystemAuthenticationProvider } from '@/auth/SystemAuthenticationProvider';
import { SessionExpiredEmptyState } from '@/components/SessionExpiredEmptyState';
import { showSessionExpiredToast } from '@/components/SessionExpiredToast';
import { type NavItem, Sidebar } from '@/components/Sidebar';
import { Topbar } from '@/components/Topbar';
import { type SessionExpiredSeverity, useAuthenticatedQueryClient } from '@/hooks';
import { SystemPermissionProvider } from '@/permissions/SystemPermissionProvider';
import { getLoginUrl, systemLogout } from '@/queries/auth';
import { getCurrentSystemUser } from '@/queries/system-users';
import { getFullName } from '@/utils';
import * as styles from './route.css';

const navItems: NavItem[] = [
    { to: '/system', icon: LayoutDashboard, label: 'ダッシュボード', exact: true },
    { to: '/system/surveys', icon: ChartArea, label: 'サーベイ分析' },
    { to: '/system/organizations', icon: Building2, label: 'クライアント' },
    { to: '/system/users', icon: Users, label: 'コンサルタント' },
    { to: '/system/reporting', icon: FileSpreadsheet, label: '集計くん' },
];

export const Route = createFileRoute('/system/_auth')({
    component: SystemLayout,
    // Re-checked on every genuine navigation into this subtree — but not on a
    // search-param-only `navigate()` that stays on the same already-active route
    // (e.g. changing a filter dropdown), which would otherwise re-run this on every
    // such interaction and could flash the full-screen empty state mid-interaction.
    // This route's own `cause` can't tell the two apart (it's 'stay' for both, since
    // this layout's own match never changes), but the full `matches` array can: a
    // genuine navigation always has at least one match entering fresh.
    beforeLoad: async ({ matches }) => {
        const isRealNavigation = matches.some((m) => m.cause === 'enter');
        if (!isRealNavigation) return;

        try {
            const user = await getCurrentSystemUser();
            return { user };
        } catch {
            return { user: null };
        }
    },
});

function SystemLayout() {
    // `beforeLoad` returns `undefined` (no `user` key) when skipping a search-param-only
    // navigation, so TanStack Router can't statically prove `user` is always present —
    // it always is by the time this renders, since the very first entry into this
    // subtree is guaranteed to hit the real fetch (see `beforeLoad` above).
    const { user } = Route.useRouteContext() as { user: SystemUser | null };
    const { queryClient, severity } = useAuthenticatedQueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <SystemApp user={user} severity={severity} />
        </QueryClientProvider>
    );
}

function SessionExpiredRedirect() {
    useEffect(() => {
        window.location.href = getLoginUrl('system');
    }, []);
    return null;
}

type SystemAppProps = {
    user: SystemUser | null;
    severity: SessionExpiredSeverity;
};

function SystemApp({ user, severity }: SystemAppProps) {
    const navigate = Route.useNavigate();
    const isRoot = !!useMatch({ from: '/system/_auth/', shouldThrow: false });

    useEffect(() => {
        if (severity !== 'none') showSessionExpiredToast(severity, 'system');
    }, [severity]);

    if (user === null) {
        if (isRoot) return <SessionExpiredRedirect />;
        return <SessionExpiredEmptyState kind="system" />;
    }

    return (
        <SystemAuthenticationProvider user={user}>
            <SystemPermissionProvider roleId={user.role.id}>
                <Sidebar navItems={navItems} />
                <div className={styles.content}>
                    <Topbar
                        userName={getFullName(user)}
                        userRole={user.role.name}
                        onMyPageClick={() => navigate({ to: '/system/profile' })}
                        onLogoutClick={systemLogout}
                    />
                    <main className={styles.main}>
                        <Outlet />
                    </main>
                </div>
            </SystemPermissionProvider>
        </SystemAuthenticationProvider>
    );
}
